import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react'
import { FloatingTooltip, getTooltipPosition } from './index'

export interface UseTooltipOptions {
  maxWidth?: number
  maxHeight?: number
  rowHeight?: number
  padding?: number
  verticalGap?: number
}

export const useTooltip = (options: UseTooltipOptions = {}) => {
  const {
    maxWidth = 360,
    maxHeight = 280,
    rowHeight = 22,
    padding = 16,
    verticalGap = 4,
  } = options

  const [visible, setVisible] = useState(false)
  const [position, setPosition] = useState({ x: 0, y: 0, transform: '' })
  const [content, setContent] = useState('')
  const [isPinned, setIsPinned] = useState(false)
  const [pinnedTarget, setPinnedTarget] = useState<HTMLElement | null>(null)
  const isPinnedRef = useRef(false)
  const currentTargetRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    isPinnedRef.current = isPinned
  }, [isPinned])

  const registeredTargets = React.useRef<Set<HTMLElement>>(new Set())

  const handleTargetClick = useCallback((e: MouseEvent) => {
    e.stopPropagation()
    const target = e.currentTarget as HTMLElement
    setIsPinned(true)
    setPinnedTarget(target)
  }, [])

  useEffect(() => {
    const targets = registeredTargets.current
    return () => {
      targets.forEach((target) => {
        target.removeAttribute('data-has-tooltip-click')
        target.removeEventListener('click', handleTargetClick)
      })
    }
  }, [handleTargetClick])

  const handleMouseLeave = useCallback(() => {
    currentTargetRef.current = null
    if (!isPinnedRef.current) {
      setVisible(false)
    }
  }, [])

  const handleMouseEnter = useCallback(
    (e: React.MouseEvent<any>, text: string) => {
      if (isPinned) return
      const target = e.currentTarget as HTMLElement

      if (target && !target.hasAttribute('data-has-tooltip-click')) {
        target.setAttribute('data-has-tooltip-click', 'true')
        target.addEventListener('click', handleTargetClick)
        registeredTargets.current.add(target)
      }

      currentTargetRef.current = target

      const targetRect = target?.getBoundingClientRect()
      const pos = getTooltipPosition(
        e.clientX,
        e.clientY,
        text,
        maxWidth,
        rowHeight,
        padding,
        targetRect,
        maxHeight,
        verticalGap,
      )

      setContent(text)
      setPosition(pos)
      setVisible(true)
    },
    [isPinned, maxWidth, maxHeight, rowHeight, padding, verticalGap, handleTargetClick],
  )

  const handleCellClick = useCallback(
    (e: React.MouseEvent<any>, text: string) => {
      e.stopPropagation()
      const target = e.currentTarget as HTMLElement
      const targetRect = target?.getBoundingClientRect()
      const pos = getTooltipPosition(
        e.clientX,
        e.clientY,
        text,
        maxWidth,
        rowHeight,
        padding,
        targetRect,
        maxHeight,
        verticalGap,
      )

      setContent(text)
      setPosition(pos)
      setVisible(true)
      setIsPinned(true)
      setPinnedTarget(target)
    },
    [maxWidth, maxHeight, rowHeight, padding, verticalGap],
  )

  useEffect(() => {
    const handleGlobalClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement

      const tooltipContainer = document.querySelector('[data-tooltip-floating]')
      if (tooltipContainer && tooltipContainer.contains(target)) {
        return
      }

      if (pinnedTarget && pinnedTarget.contains(target)) {
        return
      }

      setIsPinned(false)
      setPinnedTarget(null)
      setVisible(false)
    }

    if (isPinned) {
      document.addEventListener('mousedown', handleGlobalClick)
    }
    return () => {
      document.removeEventListener('mousedown', handleGlobalClick)
    }
  }, [isPinned, pinnedTarget])

  const createTableTooltipHandler = <T,>(getText: (record: T) => string) => {
    return (e: React.MouseEvent<HTMLElement>, record: T) => {
      const text = getText(record)
      if (!text) {
        setVisible(false)
        return
      }
      handleMouseEnter(e, text)
    }
  }

  const tooltipElement = useMemo(
    () => (
      <FloatingTooltip visible={visible} x={position.x} y={position.y} transform={position.transform} interactive={isPinned}>
        {content}
      </FloatingTooltip>
    ),
    [visible, position.x, position.y, position.transform, content, isPinned],
  )

  return {
    tooltipElement,
    handleMouseEnter,
    handleMouseLeave,
    handleCellClick,
    createTableTooltipHandler,
    isPinned,
  }
}
